import React, { useState, useEffect } from 'react';
import { 
  Brain, Calculator, Star, BookOpen, Info, 
  ArrowLeft, TrendingUp, Lightbulb, Zap
} from 'lucide-react';
import { renderMathToHTML } from '../../utils/mathRenderer';

  const AnalysisViewPage = ({ solution, onBack }) => {
    const [activeSection, setActiveSection] = useState('overview');

    // Extract dynamic content from solution data
    const getAnalysisContent = () => {
      if (!solution) return null;

      const graphData = solution.graphData;
      if (!graphData || !graphData.nodes) return null;

      // Categorize nodes by type
      const concepts = graphData.nodes.filter(node => node.type === 'concepts');
      const formulas = graphData.nodes.filter(node => node.type === 'formulas');
      const values = graphData.nodes.filter(node => node.type === 'values');
      const steps = graphData.nodes.filter(node => node.type === 'steps');

      return {
        problem: solution.equation || solution.problem || 'Mathematical problem',
        type: solution.problemType || 'algebraic',
        difficulty: solution.difficulty || 'intermediate',
        concepts,
        formulas,
        values,
        steps,
        description: solution.description || 'Mathematical problem analysis'
      };
    };

    const analysisContent = getAnalysisContent();

    // MathJax rendering effect
    useEffect(() => {
      if (window.MathJax) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          window.MathJax.typesetPromise().catch(err => {
            console.log('MathJax rendering error:', err);
          });
        }, 100);
      }
    }, [activeSection, analysisContent]);

    // Helper function to render LaTeX content using our enhanced mathRenderer
    const renderLatexContent = (content) => {
      if (!content) return '';
      
      // Use our enhanced mathRenderer for proper LaTeX rendering
      return renderMathToHTML(content);
    };

  const analysisSections = [
    {
      id: 'overview',
      title: 'Problem Overview',
      icon: Brain,
      color: 'blue',
      description: 'General analysis and problem classification'
    },
    {
      id: 'concepts',
      title: 'Mathematical Concepts',
      icon: Brain,
      color: 'indigo',
      description: 'Core mathematical ideas and principles'
    },
    {
      id: 'methods',
      title: 'Solution Methods',
      icon: Calculator,
      color: 'emerald',
      description: 'Mathematical techniques and approaches'
    },
    {
      id: 'steps',
      title: 'Solution Steps',
      icon: BookOpen,
      color: 'violet',
      description: 'Detailed step-by-step solution process'
    },
    {
      id: 'results',
      title: 'Results & Verification',
      icon: Star,
      color: 'amber',
      description: 'Final answers and verification methods'
    }
  ];

  const getIconColor = (color) => {
    const colorMap = {
      'blue': 'text-blue-600',
      'indigo': 'text-indigo-600',
      'emerald': 'text-emerald-600',
      'amber': 'text-amber-600',
      'violet': 'text-violet-600'
    };
    return colorMap[color] || 'text-gray-600';
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Solver"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mathematical Analysis</h1>
              <p className="text-sm text-gray-600">Detailed Problem Analysis & Solution</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
                       <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
             <Brain className="w-5 h-5 text-blue-600" />
             <span className="text-sm font-medium text-blue-700">Analysis Mode</span>
           </div>
           {analysisContent && (
             <div className="text-xs text-gray-500 mt-2">
               Current Problem: {analysisContent.problem}
             </div>
           )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Navigation */}
        <div className="w-80 bg-white border-r border-gray-200 shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Analysis Sections</h3>
          <div className="space-y-2">
            {analysisSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full p-4 text-left rounded-xl transition-all ${
                  activeSection === section.id
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-md'
                    : 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getIconColor(section.color).replace('text-', 'bg-').replace('-600', '-100')}`}>
                    <section.icon className={`w-5 h-5 ${getIconColor(section.color)}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{section.title}</h4>
                    <p className="text-xs text-gray-500 mt-1">{section.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Analysis Area */}
        <div className="flex-1 bg-white p-6">
          {analysisContent ? (
            <div className="h-full overflow-y-auto">
              {activeSection === 'overview' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h3 className="text-xl font-bold text-blue-900 mb-4">Problem Overview</h3>
                    <div className="space-y-4">
                                             <div>
                         <h4 className="font-semibold text-blue-800 mb-2">Problem Statement</h4>
                         <div className="text-blue-700 leading-relaxed math-rendered" 
                              dangerouslySetInnerHTML={{ __html: renderLatexContent(analysisContent.problem) }}>
                         </div>
                       </div>
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-2">Problem Type</h4>
                        <p className="text-blue-700 capitalize">
                          {analysisContent.type} equation solving with mathematical analysis.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-2">Difficulty Level</h4>
                        <p className="text-blue-700 capitalize">
                          {analysisContent.difficulty} level requiring multiple mathematical concepts.
                        </p>
                      </div>
                                             <div>
                         <h4 className="font-semibold text-blue-800 mb-2">Description</h4>
                         <div className="text-blue-700 math-rendered" 
                              dangerouslySetInnerHTML={{ __html: renderLatexContent(analysisContent.description) }}>
                         </div>
                       </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'concepts' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                    <h3 className="text-xl font-bold text-indigo-900 mb-4">Mathematical Concepts</h3>
                    <div className="grid gap-4">
                      {analysisContent.concepts.length > 0 ? (
                        analysisContent.concepts.map((concept, index) => (
                          <div key={concept.id || index} className="bg-white rounded-lg p-4 border border-indigo-200">
                                                         <h4 className="font-semibold text-indigo-800 mb-2">{concept.name}</h4>
                             <div className="text-indigo-700 text-sm mb-2 math-rendered" 
                                  dangerouslySetInnerHTML={{ __html: renderLatexContent(concept.description) }}>
                             </div>
                             {concept.value && (
                               <div className="bg-indigo-50 rounded p-2">
                                 <div className="text-indigo-800 text-sm math-rendered" 
                                      dangerouslySetInnerHTML={{ __html: renderLatexContent(concept.value) }}>
                                 </div>
                               </div>
                             )}
                          </div>
                        ))
                      ) : (
                        <div className="bg-white rounded-lg p-4 border border-indigo-200">
                          <p className="text-indigo-700 text-sm">No mathematical concepts available for this problem.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'methods' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
                    <h3 className="text-xl font-bold text-emerald-900 mb-4">Solution Methods</h3>
                    <div className="space-y-4">
                      {analysisContent.formulas.length > 0 ? (
                        analysisContent.formulas.map((formula, index) => (
                          <div key={formula.id || index} className="bg-white rounded-lg p-4 border border-emerald-200">
                                                         <h4 className="font-semibold text-emerald-800 mb-2">{formula.name}</h4>
                             <div className="text-emerald-700 text-sm mb-2 math-rendered" 
                                  dangerouslySetInnerHTML={{ __html: renderLatexContent(formula.description) }}>
                             </div>
                             {formula.value && (
                               <div className="bg-emerald-50 rounded p-2">
                                 <div className="text-emerald-800 text-sm math-rendered" 
                                      dangerouslySetInnerHTML={{ __html: renderLatexContent(formula.value) }}>
                                 </div>
                               </div>
                             )}
                          </div>
                        ))
                      ) : (
                        <div className="bg-white rounded-lg p-4 border border-emerald-200">
                          <p className="text-emerald-700 text-sm">No solution methods available for this problem.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'steps' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-200">
                    <h3 className="text-xl font-bold text-violet-900 mb-4">Solution Steps</h3>
                    <div className="space-y-4">
                      {analysisContent.steps.length > 0 ? (
                        analysisContent.steps.map((step, index) => (
                          <div key={step.id || index} className="bg-white rounded-lg p-4 border border-violet-200">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center">
                                <span className="text-violet-600 font-semibold text-sm">{index + 1}</span>
                              </div>
                                                           <h4 className="font-semibold text-violet-800">{step.name}</h4>
                           </div>
                           <div className="text-violet-700 text-sm mb-2 math-rendered" 
                                dangerouslySetInnerHTML={{ __html: renderLatexContent(step.description) }}>
                           </div>
                           {step.value && (
                             <div className="bg-violet-50 rounded p-2">
                               <div className="text-violet-800 text-sm math-rendered" 
                                    dangerouslySetInnerHTML={{ __html: renderLatexContent(step.value) }}>
                               </div>
                             </div>
                           )}
                          </div>
                        ))
                      ) : (
                        <div className="bg-white rounded-lg p-4 border border-violet-200">
                          <p className="text-violet-700 text-sm">No solution steps available for this problem.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'results' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                    <h3 className="text-xl font-bold text-amber-900 mb-4">Results & Verification</h3>
                    <div className="space-y-4">
                      {analysisContent.values.length > 0 ? (
                        analysisContent.values.map((value, index) => (
                          <div key={value.id || index} className="bg-white rounded-lg p-4 border border-amber-200">
                                                         <h4 className="font-semibold text-amber-800 mb-2">{value.name}</h4>
                             <div className="text-amber-700 text-sm mb-2 math-rendered" 
                                  dangerouslySetInnerHTML={{ __html: renderLatexContent(value.description) }}>
                             </div>
                             {value.value && (
                               <div className="bg-amber-50 rounded p-2">
                                 <div className="text-amber-800 text-sm math-rendered" 
                                      dangerouslySetInnerHTML={{ __html: renderLatexContent(value.value) }}>
                                 </div>
                               </div>
                             )}
                          </div>
                        ))
                      ) : (
                        <div className="bg-white rounded-lg p-4 border border-amber-200">
                          <p className="text-amber-700 text-sm">No results available for this problem.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-200">
                  <Brain className="w-20 h-20 mx-auto mb-6 opacity-40 text-gray-400" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">No Analysis Available</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Solve a mathematical problem to view detailed analysis and solution steps.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>Return to solver to get started</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisViewPage; 